import { useState } from "react";
import { NOTE_REVIEW_DAYS, noteIsValid, type CoverageNote } from "@/lib/coverage/coverageNotes";

type Props = {
  partyId: string | null;
  program: string;
  sizeLabel: string;
  notes: CoverageNote[];
  busy: boolean;
  error: string | null;
  onSave: (input: { partyId: string; body: string; program: string; sizeLabel: string; reviewAfter: string }) => void;
  onInvalidate: (id: string) => void;
  onReconfirm: (id: string) => void;
};

const field = "mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100";

export function CoverageNotesPanel(props: Props) {
  const [body, setBody] = useState("");
  const [program, setProgram] = useState("");
  const [sizeLabel, setSizeLabel] = useState("");
  const [reviewAfter, setReviewAfter] = useState(defaultReviewDate);
  const mine = props.notes.filter((note) => note.partyId === props.partyId);
  const now = Date.now();

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Notes</h3>
      {!props.partyId ? (
        <p className="mt-2 text-sm text-gray-600 dark:text-zinc-300">Save a plant, then write a note on it.</p>
      ) : (
        <>
          <ul className="mt-2 space-y-2">
            {mine.map((note) => (
              <NoteRow
                key={note.id}
                note={note}
                status={noteStatus(note, now, props.program, props.sizeLabel)}
                busy={props.busy}
                onInvalidate={props.onInvalidate}
                onReconfirm={props.onReconfirm}
              />
            ))}
          </ul>
          <label className="mt-3 block text-xs text-gray-500">
            Note
            <textarea className={field} rows={3} value={body} onChange={(e) => setBody(e.target.value)} />
          </label>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <label className="block text-xs text-gray-500">
              Program scope
              <input className={field} value={program} onChange={(e) => setProgram(e.target.value)} placeholder="Blank means any" />
            </label>
            <label className="block text-xs text-gray-500">
              Size scope
              <input className={field} value={sizeLabel} onChange={(e) => setSizeLabel(e.target.value)} placeholder="Blank means any" />
            </label>
          </div>
          <label className="mt-2 block text-xs text-gray-500">
            Review on
            <input className={field} type="date" value={reviewAfter} onChange={(e) => setReviewAfter(e.target.value)} />
          </label>
          <button
            type="button"
            disabled={props.busy || !body.trim()}
            className="mt-2 rounded-lg bg-gray-900 px-3 py-2 text-sm text-white disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900"
            onClick={() => {
              props.onSave({ partyId: props.partyId!, body, program, sizeLabel, reviewAfter });
              setBody("");
            }}
          >
            {props.busy ? "Saving…" : "Save note"}
          </button>
        </>
      )}
      {props.error ? <p className="mt-2 text-sm text-red-600">{props.error}</p> : null}
    </section>
  );
}

function NoteRow(props: {
  note: CoverageNote;
  status: string;
  busy: boolean;
  onInvalidate: (id: string) => void;
  onReconfirm: (id: string) => void;
}) {
  const scope = [props.note.program, props.note.sizeLabel].filter(Boolean).join(" · ");
  const status = props.status;
  return (
    <li className="rounded-md border border-gray-100 p-2 text-sm dark:border-zinc-800">
      <p className="text-gray-900 dark:text-zinc-100">{props.note.body}</p>
      <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
        {status}
        {scope ? ` · ${scope}` : ""}
      </p>
      <div className="mt-1 flex gap-3">
        {props.note.invalidatedAt ? null : (
          <button type="button" className="text-sm text-blue-700 dark:text-blue-300" disabled={props.busy} onClick={() => props.onInvalidate(props.note.id)}>
            Mark invalid
          </button>
        )}
        <button type="button" className="text-sm text-blue-700 dark:text-blue-300" disabled={props.busy} onClick={() => props.onReconfirm(props.note.id)}>
          Confirm again
        </button>
      </div>
    </li>
  );
}

function noteStatus(note: CoverageNote, nowMs: number, program: string, sizeLabel: string): string {
  if (note.invalidatedAt) return "Invalid";
  const reviewAt = Date.parse(note.reviewAfter);
  if (!Number.isFinite(reviewAt) || reviewAt <= nowMs) return "Past review";
  if (noteIsValid(note, nowMs, program, sizeLabel)) return "Counts toward the suggestion";
  return "Outside this program or size";
}

function defaultReviewDate(): string {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() + NOTE_REVIEW_DAYS);
  return date.toISOString().slice(0, 10);
}
